// ── Server Agent setup wizard page (TPT65) ──
//
// 3-step wizard: pick an agent, copy a bootstrap one-liner, watch the box connect. Backend
// is TPT64 (api/src/routes/server-agent.js). See ai/architecture/tt-server-agent.md § Web
// wizard for the design rationale (client-built command, fail-closed write gate, poll
// self-healing) — summarized in comments below at each decision point.

import { api } from '../api.js';
import { checkAuth, getUser } from '../auth.js';
import { showToast } from '../ui.js';
import { escapeHtml } from '../utils.js';
import { initLayout, getContentEl, updateLayoutContext } from '../layout.js';
import {
  AGENTS, buildAgentCommand, isPublicOrigin, relativeTime, expiresIn, deviceState,
} from '../lib/server-agent-command.js';

const STATUS_POLL_MS = 10000;
const POLL_MAX_FAILURES = 3;

let state = null;
let pollTimerId = null;
let pollVisHandler = null;
let pollInFlight = false;
let pollFailures = 0;

function resetState() {
  state = {
    projectId: null,
    canWrite: false,   // fails CLOSED when self isn't found in members — see canWriteAccess()
    agent: 'claude',
    deviceName: '',
    command: null,      // last-generated command string, or null
    generating: false,
    generateError: null,
    status: null,       // last GET /server-agent/status response, or null before first load
    statusPaused: false,
    renewDeviceId: null, // set while composing a renew (device_id path) instead of a fresh device
  };
}

// Owner or editor may generate a command (mirrors the API's requireWriteAccess: isOwner ||
// memberRole === 'editor' — server-agent.js). Deliberately FAILS CLOSED when self isn't
// found in the members list, unlike board.js's canEditWorkflow() (which fails open because
// a rejected PUT /reorder is a visible backstop there). Here POST /auth/project-token
// succeeds for any authenticated caller regardless of role, so a fail-open gate would hand
// a viewer a complete, copy-pasteable command that only dies with a bare 403 once run on a
// remote box — the worst place to discover a permission error.
function canWriteAccess(members) {
  const user = getUser();
  if (!user) return false;
  const me = members.find(m => (m.user_id || m.id) === user.id);
  return !!me && (me.role === 'owner' || me.role === 'editor');
}

// ── Status poll — self-healing (router.js never invokes a page's returned cleanup: its
// handler is async, so `route.handler(params) || null` stores a Promise, and
// `typeof currentCleanup === 'function'` is always false — see tt-web-auth.md Gotchas).
// The DOM (host presence + a data-project-id match) is the only reliable "this page is
// gone, or repainted for a different project" signal. ──

function stopStatusPoll() {
  if (pollTimerId) { clearInterval(pollTimerId); pollTimerId = null; }
  if (pollVisHandler) { document.removeEventListener('visibilitychange', pollVisHandler); pollVisHandler = null; }
  pollInFlight = false;
  pollFailures = 0;
}

async function statusTick() {
  const host = document.getElementById('sa-status');
  if (!host || !host.isConnected || host.dataset.projectId !== String(state.projectId)) {
    stopStatusPoll();
    return;
  }
  if (document.hidden || pollInFlight) return;

  pollInFlight = true;
  const tickProjectId = state.projectId;
  try {
    const res = await api.get(`/projects/${tickProjectId}/server-agent/status`);
    const still = document.getElementById('sa-status'); // late-resolve guard — user may have navigated mid-flight
    if (!still || !still.isConnected || still.dataset.projectId !== String(tickProjectId)) return;
    pollFailures = 0;
    state.status = res;
    state.statusPaused = false;
    renderStatus();
  } catch {
    if (++pollFailures >= POLL_MAX_FAILURES) {
      stopStatusPoll();
      state.statusPaused = true;
      renderStatus();
    }
  } finally {
    pollInFlight = false;
  }
}

function startStatusPoll() {
  stopStatusPoll(); // idempotent restart — also the guard against cross-project double-polling
  statusTick();      // paint immediately, don't wait a full interval
  pollTimerId = setInterval(statusTick, STATUS_POLL_MS);
  pollVisHandler = () => { if (!document.hidden) statusTick(); };
  document.addEventListener('visibilitychange', pollVisHandler);
}

// ── Render ──

function renderAgentOptions() {
  return AGENTS.map(a => {
    const checked = state.agent === a.id ? ' checked' : '';
    const disabled = a.available ? '' : ' disabled';
    const badge = a.available ? '' : '<span class="badge under-construction-badge">Under construction</span>';
    return `<label class="server-agent-agent">
      <input type="radio" name="sa-agent" value="${escapeHtml(a.id)}"${checked}${disabled}>
      <span class="server-agent-agent-dot" style="--dot:${a.color}"></span>
      <span class="server-agent-agent-label">${escapeHtml(a.label)}</span>
      ${badge}
    </label>`;
  }).join('');
}

function renderStep2Body() {
  if (!state.canWrite) {
    return `<p class="text-muted server-agent-hint">Only project owners and editors can generate a server agent command. Ask one of them to set this up, or watch Step 3 below once it's running.</p>`;
  }

  const origin = window.location.origin;
  const originCheck = isPublicOrigin(origin);
  const originWarnHtml = !originCheck.public
    ? `<div class="server-agent-warn" id="sa-origin-warn">This page is served from <code>${escapeHtml(origin)}</code>, which a remote server can't reach. Open the Server Agent page on this project's public Tipatask URL instead, then generate the command there. (The command below will still work for testing a box that CAN reach this address.)</div>`
    : originCheck.insecure
      ? `<div class="server-agent-warn" id="sa-origin-warn">This page is served over plain <code>http://</code> — the access token below would cross the network unencrypted. Use an <code>https://</code> URL if one is available.</div>`
      : '';

  const renewNotice = state.renewDeviceId
    ? `<p class="text-muted server-agent-hint">Renewing the command for this device — it will reuse the same row instead of creating a new one.</p>`
    : '';

  const nameValue = escapeHtml(state.deviceName || '');
  const trimmedLen = (state.deviceName || '').trim().length;
  const genDisabled = state.generating || trimmedLen === 0 || trimmedLen > 255 ? ' disabled' : '';

  const errorHtml = state.generateError
    ? `<div class="server-agent-error" id="sa-step2-error">${escapeHtml(state.generateError)}</div>`
    : '';

  const cmdHtml = state.command
    ? `<div class="server-agent-cmd-wrap" id="sa-cmd-wrap">
        <div class="server-agent-warn">This command embeds a live 7-day access token — treat it like a password. It also stays in your shell history once pasted; there is no way to revoke it early, only to regenerate a fresh one.</div>
        <div class="server-agent-cmd">
          <pre class="server-agent-cmd-pre" id="sa-cmd" tabindex="0">${escapeHtml(state.command)}</pre>
          <button class="btn btn-ghost btn-sm server-agent-copy" id="sa-copy-btn" type="button">Copy</button>
        </div>
        <p class="text-muted server-agent-hint">Run it on the target server over SSH. It configures Claude Code with tipatask's MCP server and knowledge base, then checks in — Step 3 below will flip to Connected once it does.</p>
      </div>`
    : '';

  return `${renewNotice}
    ${originWarnHtml}
    <label class="form-label" for="sa-device-name">Server name</label>
    <input class="form-input server-agent-name-input" id="sa-device-name" placeholder="production-server" maxlength="255" autocomplete="off" value="${nameValue}">
    <p class="text-muted server-agent-hint">A name to identify this box in Step 3 below.</p>
    <button class="btn btn-primary" id="sa-generate-btn" type="button"${genDisabled}>${state.renewDeviceId ? 'Renew command' : 'Generate command'}</button>
    ${errorHtml}
    ${cmdHtml}`;
}

function renderDeviceRow(device) {
  const { key, label } = deviceState(device);
  const colorVar = { connected: 'var(--c-green)', idle: 'var(--c-overlay1)', expired: 'var(--c-red)', waiting: 'var(--c-overlay0)' }[key];
  const lastSeen = relativeTime(device.last_seen_at) || 'never';
  const expiry = device.token_expires_at
    ? (device.token_expired ? 'token expired' : `token expires ${expiresIn(device.token_expires_at)}`)
    : 'no token recorded';
  const renewBtn = state.canWrite
    ? `<button class="btn btn-ghost btn-sm" data-renew-device-id="${device.id}" type="button">Renew command</button>`
    : '';
  return `<li class="server-agent-device">
    <span class="server-agent-device-name">${escapeHtml(device.name)}</span>
    <span class="badge badge-status" style="--status-color:${colorVar}">${escapeHtml(label)}</span>
    <span class="server-agent-device-meta">Last seen ${escapeHtml(lastSeen)} · ${escapeHtml(expiry)}</span>
    <span class="server-agent-device-actions">${renewBtn}</span>
  </li>`;
}

function renderStatus() {
  const host = document.getElementById('sa-status');
  if (!host) return;

  if (state.statusPaused) {
    host.innerHTML = `<div class="empty text-muted">Live updates paused after repeated errors. <button class="btn btn-ghost btn-sm" id="sa-status-refresh" type="button">Refresh</button></div>`;
    return;
  }

  const status = state.status || { devices: [], connected: false };
  const devicesHtml = status.devices.length
    ? `<ul class="server-agent-devices">${status.devices.map(renderDeviceRow).join('')}</ul>
       <p class="text-muted server-agent-hint">Only the teammate who set a server agent up can renew its command — anyone else's attempt fails with "Server device not found".</p>`
    : `<div class="empty text-muted">No server agent has checked in yet — run the command above on your server.</div>`;

  host.innerHTML = `<div class="server-agent-conn" data-connected="${status.connected}">
      <span class="server-agent-dot"></span>
      <span class="server-agent-conn-label">${status.connected ? 'Connected' : 'Not connected'}</span>
    </div>
    ${devicesHtml}`;
}

function render() {
  const content = getContentEl();
  content.innerHTML = `
    <div class="page-header"><h2>Server Agent</h2></div>
    <div class="server-agent-page">
      <p class="text-muted server-agent-intro">Set up a headless, tipatask-connected Claude CLI on a remote server for deployments, server-side maintenance, and contributing to this project's knowledge base.</p>

      <section class="card server-agent-step">
        <div class="server-agent-step-head">
          <span class="server-agent-step-num" aria-hidden="true">1</span>
          <h3 class="card-title">Choose an agent</h3>
        </div>
        <div class="server-agent-step-body">
          <div class="server-agent-agents">${renderAgentOptions()}</div>
        </div>
      </section>

      <section class="card server-agent-step${state.canWrite ? '' : ' is-locked'}">
        <div class="server-agent-step-head">
          <span class="server-agent-step-num" aria-hidden="true">2</span>
          <h3 class="card-title">Copy the command</h3>
        </div>
        <div class="server-agent-step-body" id="sa-step2-body">${renderStep2Body()}</div>
      </section>

      <section class="card server-agent-step">
        <div class="server-agent-step-head">
          <span class="server-agent-step-num" aria-hidden="true">3</span>
          <h3 class="card-title">Connection</h3>
        </div>
        <div class="server-agent-step-body" id="sa-status" data-project-id="${state.projectId}" aria-live="polite">
          <div class="empty"><span class="spinner"></span></div>
        </div>
      </section>
    </div>`;

  renderStatus();
}

// ── Actions ──

async function generateCommand() {
  const input = document.getElementById('sa-device-name');
  const deviceName = (input ? input.value : state.deviceName || '').trim();
  if (!deviceName) return;

  state.deviceName = deviceName;
  state.generating = true;
  state.generateError = null;
  const step2 = document.getElementById('sa-step2-body');
  if (step2) step2.innerHTML = renderStep2Body();

  try {
    const { token } = await api.post('/auth/project-token', { project_id: Number(state.projectId) });
    const agentInput = document.querySelector('input[name="sa-agent"]:checked');
    const agent = agentInput ? agentInput.value : state.agent;
    state.command = state.renewDeviceId
      ? buildAgentCommand({ origin: window.location.origin, projectId: state.projectId, agent, token, deviceId: state.renewDeviceId })
      : buildAgentCommand({ origin: window.location.origin, projectId: state.projectId, agent, token, deviceName });
  } catch (err) {
    state.command = null;
    state.generateError = err.body?.error || err.message || 'Failed to generate command';
  } finally {
    state.generating = false;
    const body = document.getElementById('sa-step2-body');
    if (body) body.innerHTML = renderStep2Body();
  }
}

async function copyCommand() {
  if (!state.command) return;
  try {
    await navigator.clipboard.writeText(state.command);
    showToast('Command copied', 'success');
  } catch {
    showToast('Copy failed — select and copy manually', 'error');
  }
}

function startRenew(deviceId) {
  state.renewDeviceId = deviceId;
  state.command = null;
  state.generateError = null;
  const body = document.getElementById('sa-step2-body');
  if (body) body.innerHTML = renderStep2Body();
  document.getElementById('sa-step2-body')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function onClick(e) {
  if (e.target.closest('#sa-generate-btn')) { generateCommand(); return; }
  if (e.target.closest('#sa-copy-btn')) { copyCommand(); return; }
  if (e.target.closest('#sa-status-refresh')) { state.statusPaused = false; startStatusPoll(); return; }
  const renewBtn = e.target.closest('[data-renew-device-id]');
  if (renewBtn) { startRenew(Number(renewBtn.dataset.renewDeviceId)); return; }
}

function onInput(e) {
  if (e.target.id !== 'sa-device-name') return;
  // Any edit to the name after a command was generated invalidates the stale command
  // rather than leaving it visible under a now-different name.
  state.deviceName = e.target.value;
  state.command = null;
  state.generateError = null;
  const btn = document.getElementById('sa-generate-btn');
  if (btn) btn.disabled = state.deviceName.trim().length === 0 || state.deviceName.trim().length > 255;
  const wrap = document.getElementById('sa-cmd-wrap');
  if (wrap) wrap.remove();
}

// ── Page entry ──

export async function renderServerAgentPage(params) {
  if (!(await checkAuth())) return;

  resetState();
  state.projectId = params.id;

  initLayout(getUser());
  updateLayoutContext({ projectId: params.id, projectName: null, activeView: 'server-agent', viewLabel: 'Server Agent' });

  const content = getContentEl();
  content.innerHTML = `<div class="page-header"><h2>Server Agent</h2></div><div class="empty"><span class="spinner"></span></div>`;

  try {
    const [membersRes, statusRes] = await Promise.all([
      api.get('/projects/' + params.id + '/members'),
      api.get('/projects/' + params.id + '/server-agent/status'),
    ]);
    state.canWrite = canWriteAccess(membersRes.members || []);
    state.status = statusRes;
  } catch (err) {
    content.innerHTML = `<div class="page-header"><h2>Server Agent</h2></div><div class="empty text-muted">Failed to load — ${escapeHtml(err.message || 'unknown error')}</div>`;
    return;
  }

  render();
  content.addEventListener('click', onClick);
  content.addEventListener('input', onInput);
  startStatusPoll();

  return () => {
    stopStatusPoll();
    content.removeEventListener('click', onClick);
    content.removeEventListener('input', onInput);
    resetState();
  };
}
